/**
 * Audio notification utilities for chat application
 */

export interface AudioNotificationOptions {
  volume?: number;
  loop?: boolean;
  preload?: boolean;
}

export class AudioNotificationManager {
  private static instance: AudioNotificationManager;
  private audioContext: AudioContext | null = null;
  private lastPlayedTime: number = 0;
  private readonly COOLDOWN_PERIOD = 2000; // 2 seconds cooldown between sounds
  private readonly BEEP_COOLDOWN_PERIOD = 1000; // 1 second cooldown for beep sounds
  private isInitialized: boolean = false;
  private initializationAttempted: boolean = false;

  private constructor() {
    // Don't initialize AudioContext immediately - wait for user gesture
    this.setupUserGestureHandlers();
  }

  public static getInstance(): AudioNotificationManager {
    if (!AudioNotificationManager.instance) {
      AudioNotificationManager.instance = new AudioNotificationManager();
    }
    return AudioNotificationManager.instance;
  }

  /**
   * Setup user gesture handlers to initialize AudioContext
   */
  private setupUserGestureHandlers(): void {
    const initOnUserGesture = () => {
      if (!this.isInitialized && !this.initializationAttempted) {
        this.initializeAudioContext();
        this.isInitialized = true;
        this.initializationAttempted = true;
        
        // Remove event listeners after first initialization
        document.removeEventListener('click', initOnUserGesture);
        document.removeEventListener('keydown', initOnUserGesture);
        document.removeEventListener('touchstart', initOnUserGesture);
      }
    };

    // Listen for user gestures
    document.addEventListener('click', initOnUserGesture, { once: true });
    document.addEventListener('keydown', initOnUserGesture, { once: true });
    document.addEventListener('touchstart', initOnUserGesture, { once: true });
  }

  private initializeAudioContext(): void {
    try {
      // Create audio context for generating sounds
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (error) {
      console.warn('Audio context not supported:', error);
    }
  }

  /**
   * Play a deep notification sound
   */
  public playDeepNotification(options: AudioNotificationOptions = {}): void {
    const now = Date.now();
    
    // Prevent spam by enforcing cooldown period
    if (now - this.lastPlayedTime < this.COOLDOWN_PERIOD) {
      return;
    }

    this.lastPlayedTime = now;

    // Try to initialize AudioContext if not already done
    if (!this.isInitialized && !this.initializationAttempted) {
      this.initializeAudioContext();
      this.isInitialized = true;
      this.initializationAttempted = true;
    }

    if (!this.audioContext) {
      console.warn('Audio context not available - user gesture required');
      return;
    }

    try {
      // Resume audio context if suspended (required by some browsers)
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      this.generateDeepSound(options);
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  }

  private generateDeepSound(options: AudioNotificationOptions): void {
    if (!this.audioContext) return;

    const volume = options.volume || 0.3;
    const duration = 0.8; // Duration in seconds
    const sampleRate = this.audioContext.sampleRate;
    const bufferSize = sampleRate * duration;
    
    // Create audio buffer
    const buffer = this.audioContext.createBuffer(1, bufferSize, sampleRate);
    const data = buffer.getChannelData(0);

    // Generate a deep, resonant sound using multiple sine waves
    for (let i = 0; i < bufferSize; i++) {
      const t = i / sampleRate;
      
      // Base frequency (deep tone)
      const baseFreq = 80; // Hz - deep bass
      
      // Create a complex waveform with harmonics for richness
      let sample = 0;
      
      // Fundamental frequency
      sample += Math.sin(2 * Math.PI * baseFreq * t) * 0.4;
      
      // First harmonic (octave)
      sample += Math.sin(2 * Math.PI * baseFreq * 2 * t) * 0.2;
      
      // Second harmonic
      sample += Math.sin(2 * Math.PI * baseFreq * 3 * t) * 0.1;
      
      // Add some low-frequency modulation for depth
      const modulationFreq = 2; // Hz
      const modulation = Math.sin(2 * Math.PI * modulationFreq * t) * 0.1;
      sample += modulation;
      
      // Apply envelope (fade in/out)
      const envelope = this.createEnvelope(t, duration);
      sample *= envelope;
      
      // Apply volume
      sample *= volume;
      
      data[i] = sample;
    }

    // Play the sound
    const source = this.audioContext.createBufferSource();
    const gainNode = this.audioContext.createGain();
    
    source.buffer = buffer;
    source.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    // Set volume
    gainNode.gain.value = volume;
    
    // Play
    source.start();
    
    // Clean up
    source.onended = () => {
      source.disconnect();
      gainNode.disconnect();
    };
  }

  private createEnvelope(time: number, duration: number): number {
    // Create a smooth envelope: quick attack, slow decay
    const attackTime = 0.1; // 10% of duration
    const decayTime = 0.9; // 90% of duration
    
    if (time < attackTime * duration) {
      // Attack phase - quick rise
      return time / (attackTime * duration);
    } else if (time < decayTime * duration) {
      // Decay phase - slow fall
      const decayProgress = (time - attackTime * duration) / ((decayTime - attackTime) * duration);
      return 1 - (decayProgress * 0.7); // Decay to 30% of max volume
    } else {
      // Release phase - quick fade out
      const releaseProgress = (time - decayTime * duration) / ((1 - decayTime) * duration);
      return 0.3 * (1 - releaseProgress);
    }
  }

  /**
   * Play a simple beep sound (alternative notification)
   */
  public playBeep(options: AudioNotificationOptions = {}): void {
    const now = Date.now();
    
    if (now - this.lastPlayedTime < this.BEEP_COOLDOWN_PERIOD) {
      return;
    }

    this.lastPlayedTime = now;

    // Try to initialize AudioContext if not already done
    if (!this.isInitialized && !this.initializationAttempted) {
      this.initializeAudioContext();
      this.isInitialized = true;
      this.initializationAttempted = true;
    }

    if (!this.audioContext) {
      console.warn('Audio context not available - user gesture required');
      return;
    }

    try {
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      // Deep frequency
      oscillator.frequency.setValueAtTime(200, this.audioContext.currentTime);
      oscillator.type = 'sine';
      
      // Volume envelope
      const volume = options.volume || 0.2;
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, this.audioContext.currentTime + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.5);
      
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.5);
      
      oscillator.onended = () => {
        oscillator.disconnect();
        gainNode.disconnect();
      };
    } catch (error) {
      console.error('Error playing beep sound:', error);
    }
  }

  /**
   * Check if audio is supported
   */
  public isAudioSupported(): boolean {
    return !!(window.AudioContext || (window as any).webkitAudioContext);
  }

  /**
   * Enable/disable audio notifications
   */
  public setEnabled(enabled: boolean): void {
    // Store preference in localStorage
    localStorage.setItem('audioNotificationsEnabled', enabled.toString());
  }

  /**
   * Check if audio notifications are enabled
   */
  public isEnabled(): boolean {
    const stored = localStorage.getItem('audioNotificationsEnabled');
    return stored !== 'false'; // Default to enabled
  }

  /**
   * Manually initialize AudioContext (call this on user gesture)
   */
  public initializeOnUserGesture(): void {
    if (!this.isInitialized && !this.initializationAttempted) {
      this.initializeAudioContext();
      this.isInitialized = true;
      this.initializationAttempted = true;
    }
  }
}

// Export singleton instance
export const audioNotificationManager = AudioNotificationManager.getInstance();
